import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, Check } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SERVICES, WHAT_TO_EXPECT, FAQS, IMAGES } from "@/data/content";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const Services = () => {
  return (
    <div data-testid="services-page">
      {/* Header */}
      <section className="pt-16 pb-10 lg:pt-24 lg:pb-14 bg-white border-b border-[#6B8F71]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#6B8F71] mb-4">My Services</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#355C4A]">
            Support tailored to your journey
          </h1>
          <p className="mt-5 text-lg font-light text-[#5A655F] max-w-2xl mx-auto">
            Counselling is tailored to your individual needs while respecting your pace, values, and personal experiences.
          </p>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = Icons[s.icon] || Icons.Sparkles;
              return (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={fade}
                  data-testid={`service-card-${i}`}
                  className="group bg-white rounded-[2rem] p-8 border border-[#6B8F71]/10 shadow-[0_8px_30px_rgba(107,143,113,0.05)] hover:shadow-[0_12px_40px_rgba(107,143,113,0.14)] hover:-translate-y-1.5 transition-all duration-300"
                >
                  <span className="flex items-center justify-center w-14 h-14 rounded-2xl bg-[#6B8F71]/12 text-[#355C4A] group-hover:bg-[#6B8F71] group-hover:text-white transition-colors duration-300 mb-6">
                    <Icon strokeWidth={1.5} className="w-7 h-7" />
                  </span>
                  <h3 className="font-serif text-2xl text-[#355C4A] mb-3">{s.title}</h3>
                  <p className="text-[#2F2F2F]/80 font-light leading-relaxed">{s.desc}</p>
                </motion.div>
              );
            })}

            {/* Visual card to complete the bento grid */}
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={fade}
              className="relative overflow-hidden rounded-[2rem] min-h-[240px]"
            >
              <img src={IMAGES.zen} alt="Balance and calm" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#355C4A]/80 to-[#355C4A]/20" />
              <div className="relative h-full flex flex-col justify-end p-8">
                <p className="font-serif text-2xl text-white leading-tight">Balance, at your own pace</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={fade}>
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#6B8F71] mb-5">{WHAT_TO_EXPECT.overline}</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-[#355C4A]">{WHAT_TO_EXPECT.title}</h2>
            <p className="mt-5 text-lg font-light text-[#2F2F2F]/85">{WHAT_TO_EXPECT.intro}</p>
            <p className="mt-4 text-[#5A655F] font-light">{WHAT_TO_EXPECT.close}</p>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={fade} className="space-y-4">
            {WHAT_TO_EXPECT.steps.map((step, i) => (
              <div key={i} className="flex items-center gap-5 bg-[#FAF8F4] rounded-2xl p-5 border border-[#6B8F71]/10">
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#6B8F71] text-white font-serif text-xl shrink-0">{i + 1}</span>
                <span className="text-[#2F2F2F] font-light text-lg">{step}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#6B8F71] mb-4">FAQ</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-[#355C4A]">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#6B8F71]/20">
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="text-left font-serif text-xl text-[#355C4A] hover:no-underline py-5"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#2F2F2F]/80 font-light text-base leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="rounded-[2rem] bg-[#6B8F71] px-8 py-14 sm:px-16 text-center">
            <h2 className="font-serif text-3xl sm:text-4xl font-light text-white">Not sure which service fits?</h2>
            <p className="mt-3 text-white/90 font-light text-lg">Reach out — your first session is simply a conversation.</p>
            <Link
              to="/contact"
              data-testid="services-cta-btn"
              className="mt-7 inline-flex items-center gap-2 bg-white text-[#355C4A] px-8 py-4 rounded-full hover:-translate-y-0.5 transition-all duration-300 font-medium"
            >
              Book Your Appointment <ArrowRight strokeWidth={1.75} className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
